import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';

import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import Refresh from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';

import AllProjects from './utility/AllProjects';
import { getSessionIdToken } from './auth/apiAuth';
import { retrieveSignedUrl } from './helpers/operations';

import { DataGrid } from '@mui/x-data-grid';

import axios from 'axios';

const marginsHistory = { marginTop: 32, marginBottom: 32, marginLeft: 32, marginRight: 32 };

const TODAY = dayjs().startOf('day');

function inactivityDays(dateStr) {
  if (!dateStr) return null;
  return TODAY.diff(dayjs(dateStr), 'day');
}

function silenceRatio(days, median) {
  if (days === null || !median || median <= 0) return null;
  return days / median;
}

function silenceColor(ratio) {
  if (ratio === null || ratio === undefined) return 'inherit';
  if (ratio > 3) return 'error';
  if (ratio >= 1.5) return 'warning';
  return 'success';
}

function buildRows(projects) {
  return Object.entries(projects).map(([projectId, p]) => {
    const inact = inactivityDays(p.last_job);
    const inact_client = inactivityDays(p.last_job_client);
    const sr = silenceRatio(inact, p.median_interval_days);
    const sr_client = silenceRatio(inact_client, p.median_interval_days_client_only);
    const keeping_alive =
      p.last_job_pagination && p.last_job_client
        ? dayjs(p.last_job_pagination).isAfter(dayjs(p.last_job_client))
        : false;

    const slash = projectId.indexOf('/');
    return {
      id: projectId,
      customer: slash >= 0 ? projectId.slice(0, slash) : projectId,
      project_name: slash >= 0 ? projectId.slice(slash + 1) : '',
      last_job: p.last_job || null,
      last_job_client: p.last_job_client || null,
      inactivity_days: inact,
      inactivity_days_client: inact_client,
      median_interval_days: p.median_interval_days ?? null,
      silence_ratio: sr,
      silence_ratio_client: sr_client,
      confidence: p.confidence || 'insufficient_data',
      pagination_pct: p.pagination_pct ?? null,
      n_jobs_total: p.n_jobs_total ?? 0,
      keeping_alive,
      hidden: p.hidden ?? false,
      active_months: p.active_months || [],
    };
  });
}


export default function Stats({ auth }) {
  const navbarRef = useRef();
  const theme = useTheme();

  const [rows, setRows] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const [generatedAt, setGeneratedAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState('');

  useEffect(() => {
    if (auth.userState.userItem) loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const signed = await retrieveSignedUrl({
        key: 'Pagination-WebApp/churn-scores.json',
        bucket: 'documents.pagination.com',
        ssmParams: auth.userState.ssmParams,
      });
      const res = await fetch(signed.data.signedUrl);
      const data = await res.json();
      setGeneratedAt(data.generated_at || '');
      setRows(buildRows(data.projects || {}));
    } catch (err) {
      console.error('Error fetching churn scores', err);
    } finally {
      setLoading(false);
    }
  };

  const triggerRefresh = async () => {
    setRefreshing(true);
    setRefreshMsg('Avvio ricalcolo… può richiedere 30-60 secondi.');
    try {
      const token = await getSessionIdToken();
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/churn-update`,
        {},
        {
          headers: {
            'user_id': auth.userState.user,
            'content-type': 'application/json',
            'Authorization': token,
          },
          timeout: 90000,
        }
      );
      setRefreshMsg('Ricalcolo completato. Ricarico i dati…');
      await loadData();
      setRefreshMsg('');
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        setRefreshMsg('Timeout: la Lambda sta ancora elaborando. Riprova il refresh tra qualche secondo.');
      } else {
        setRefreshMsg('Errore durante il ricalcolo.');
        console.error('Refresh error', err);
      }
    } finally {
      setRefreshing(false);
    }
  };

  const toggleHidden = async (row) => {
    const newHidden = !row.hidden;
    // ottimistic update
    setRows(prev => prev.map(r => r.id === row.id ? { ...r, hidden: newHidden } : r));
    try {
      const token = await getSessionIdToken();
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/churn-update`,
        { action: 'hide', project_id: row.id, hidden: newHidden },
        {
          headers: {
            'user_id': auth.userState.user,
            'content-type': 'application/json',
            'Authorization': token,
          },
          timeout: 15000,
        }
      );
    } catch (err) {
      console.error('Error toggling hidden', err);
      // rollback
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, hidden: row.hidden } : r));
    }
  };

  const displayRows = showHidden ? rows : rows.filter(r => !r.hidden);

  const exportCsv = () => {
    const fields = [
      'id', 'customer', 'project_name',
      'last_job', 'last_job_client',
      'inactivity_days', 'inactivity_days_client',
      'median_interval_days',
      'silence_ratio', 'silence_ratio_client',
      'confidence', 'pagination_pct', 'n_jobs_total',
      'keeping_alive', 'hidden',
    ];
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      fields.join(','),
      ...rows.map(r => fields.map(f => escape(r[f])).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `health-score-${generatedAt || dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      field: 'customer',
      renderHeader: () => <strong>Customer</strong>,
      flex: 1.5,
      minWidth: 120,
    },
    {
      field: 'project_name',
      renderHeader: () => <strong>Project</strong>,
      flex: 2,
      minWidth: 150,
    },
    {
      field: 'last_job',
      renderHeader: () => <strong>Last job</strong>,
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        const lastJob = params.value || '—';
        const lastJobClient = params.row.last_job_client;
        const keepingAlive = params.row.keeping_alive;
        const paginationPct = params.row.pagination_pct;
        const differentClient = lastJobClient && lastJobClient !== params.value;

        const tooltipLines = [];
        if (differentClient) tooltipLines.push(`Last job (client): ${lastJobClient}`);
        if (keepingAlive) tooltipLines.push('Pagination is keeping this project alive: the last client job is older than the last pagination job.');

        const cell = (
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            sx={{ width: '100%', px: 0.5 }}
          >
            <span>{lastJob}</span>
            {(differentClient || keepingAlive) && (
              <WarningAmberIcon color='warning' sx={{ fontSize: '1.1rem' }} />
            )}
          </Stack>
        );

        return tooltipLines.length > 0
          ? <Tooltip title={tooltipLines.join(' · ')}><Box sx={{ width: '100%' }}>{cell}</Box></Tooltip>
          : cell;
      },
    },
    {
      field: 'inactivity_days',
      renderHeader: () => <strong>Inactivity days</strong>,
      type: 'number',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <strong>{params.value !== null && params.value !== undefined ? params.value : '—'}</strong>
      ),
    },
    {
      field: 'silence_ratio',
      renderHeader: () => <strong>Silence ratio</strong>,
      type: 'number',
      flex: 1.5,
      minWidth: 120,
      renderCell: (params) => {
        const val = params.value;
        if (val === null || val === undefined) return <Typography variant='caption'>—</Typography>;
        const color = silenceColor(val);
        const pct = Math.min((val / 4) * 100, 100);
        return (
          <Box sx={{ width: '100%', pr: 1 }}>
            <LinearProgress sx={{ height: 11, borderRadius: 0 }} variant='determinate' color={color} value={pct} />
            <Typography variant='caption' sx={{ display: 'block', textAlign: 'right', lineHeight: 1.2 }}>
              {val.toFixed(2)}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'active_months',
      renderHeader: () => <strong>Activity (12 mo)</strong>,
      width: 160,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const activeSet = new Set(params.value || []);
        const allMonths = (params.value || []).sort();
        const firstMonth = allMonths.length > 0 ? allMonths[0] : null;
        const months = Array.from({ length: 12 }, (_, i) =>
          dayjs().subtract(11 - i, 'month').format('YYYY-MM')
        );
        return (
          <Tooltip title={months.map(m => `${m}: ${activeSet.has(m) ? '✓' : '—'}`).join('  ')}>
            <Stack direction='row' spacing={0.4} alignItems='center'>
              {months.map(m => {
                const beforeStart = firstMonth && m < firstMonth;
                return (
                  <React.Fragment key={m}>
                    {m.endsWith('-01') && (
                      <Box sx={{ width: '1px', height: 11, backgroundColor: 'neutral.main', flexShrink: 0, mx: 0.2 }} />
                    )}
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        flexShrink: 0,
                        ...(beforeStart
                          ? { border: '1px solid #cccccc', backgroundColor: 'transparent' }
                          : { backgroundColor: activeSet.has(m) ? theme.palette.primary.main : '#cccccc' }
                        ),
                      }}
                    />
                  </React.Fragment>
                );
              })}
            </Stack>
          </Tooltip>
        );
      },
    },
    {
      field: 'pagination_pct',
      renderHeader: () => <strong>Client autonomy</strong>,
      flex: 1.2,
      minWidth: 110,
      renderCell: (params) => {
        const val = params.value;
        if (val === null || val === undefined) return <Typography variant='caption'>—</Typography>;
        const autonomy = 100 - val;
        return (
          <Tooltip title={`Client-driven jobs: ${Math.round(autonomy)}% — Pagination-driven: ${Math.round(val)}%`}>
            <Box sx={{ width: '100%', pr: 1 }}>
              <LinearProgress
                sx={{
                  height: 6,
                  borderRadius: 0,
                  backgroundColor: theme.palette.neutral.light,
                  '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.neutral.main },
                }}
                variant='determinate'
                value={autonomy}
              />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'confidence',
      renderHeader: () => <Tooltip title='Confidence — based on number of active months on record'><QueryStatsIcon fontSize='small' /></Tooltip>,
      width: 28,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const conf = params.value;
        if (conf === 'high') return null;
        const tooltip = conf === 'insufficient_data'
          ? 'Low confidence: fewer than 2 active months on record — this estimate is not reliable.'
          : 'Low confidence: fewer than 5 active months on record — treat this estimate with caution.';
        return (
          <Tooltip title={tooltip}>
            <WarningAmberIcon sx={{ fontSize: '1.1rem' }} color='warning' />
          </Tooltip>
        );
      },
    },
    {
      field: 'hidden',
      renderHeader: () => '',
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title={params.value ? 'Rendi visibile' : 'Nascondi dalla lista'}>
          <IconButton size='small' onClick={() => toggleHidden(params.row)}>
            {params.value
              ? <VisibilityOffIcon fontSize='small' color='disabled' />
              : <VisibilityIcon fontSize='small' />}
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ minWidth: '1300px' }}>
      <Stack
        direction='column'
        sx={{ margin: { ...marginsHistory, marginTop: marginsHistory.marginTop + ((navbarRef.current && navbarRef.current.clientHeight) || 0) } }}
        spacing={4}
      >
        <Card sx={{ padding: '1em' }}>
          {/* header */}
          <Stack sx={{ m: '1em' }} direction='row' alignItems='center' justifyContent='space-between' spacing={2}>
            <Stack direction='row' alignItems='baseline' spacing={2}>
              <Typography variant='h5' color='primary'>
                <b>Health score</b>
              </Typography>
              {generatedAt && (
                <Typography variant='caption' color='text.secondary'>
                  Last update: {generatedAt}
                </Typography>
              )}
            </Stack>
            <Stack direction='row' alignItems='center' spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showHidden}
                    onChange={(e) => setShowHidden(e.target.checked)}
                    size='small'
                  />
                }
                label={<Typography variant='body2'>Show all</Typography>}
              />
              <Tooltip title='Download CSV (all columns)'>
                <span>
                  <IconButton onClick={exportCsv} disabled={loading || rows.length === 0}>
                    <DownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={refreshing ? 'Ricalcolo in corso…' : 'Ricalcola churn scores e ricarica'}>
                <span>
                  <IconButton onClick={triggerRefresh} disabled={refreshing || loading}>
                    {refreshing ? <CircularProgress size='1.2rem' /> : <Refresh />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          {/* description */}
          <Stack sx={{ mx: '1em', mb: '0.5em' }} spacing={0.5}>
            <Typography variant='caption' color='text.secondary'>
              Each project has a rhythm — some clients paginate every month, others once a year. To measure inactivity fairly,
              we identify the months in which a project had at least one job, then compute the median gap between those months.
              This becomes the project's "typical interval." The <b>silence ratio</b> divides the days since the last job by that typical interval.
              A ratio of 1.0 means the project has been silent for exactly as long as usual — nothing alarming.
              A ratio of 2.0 means it has been quiet for twice its normal cycle. Above 3.0, the project is well outside its historical pattern and likely needs attention.
              This approach avoids penalising projects that are naturally infrequent: a yearly client with a ratio of 0.8 is more "on track" than a monthly client with a ratio of 1.2.
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              <b>Silence ratio</b> — bar reaches 100% at 4× the median.
              Color: <span style={{ color: '#2e7d32' }}>green</span> below 1.5×,{' '}
              <span style={{ color: '#ed6c02' }}>orange</span> 1.5–3×,{' '}
              <span style={{ color: '#d32f2f' }}>red</span> above 3×.
            </Typography>
          </Stack>

          {/* status messages */}
          {(loading || refreshMsg) && (
            <Stack sx={{ mx: '1em', mb: '0.5em' }} direction='row' alignItems='center' spacing={1}>
              {(loading || refreshing) && <CircularProgress size='0.9rem' />}
              <Typography
                variant='caption'
                color={refreshMsg.includes('Errore') || refreshMsg.includes('Timeout') ? 'error' : 'primary'}
              >
                {refreshMsg || 'Caricamento dati…'}
              </Typography>
            </Stack>
          )}

          <CardContent>
            <DataGrid
              rows={displayRows}
              columns={columns}
              density='compact'
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
                sorting: { sortModel: [{ field: 'silence_ratio', sort: 'desc' }] },
              }}
              pageSizeOptions={[25, 50, 100]}
              disableRowSelectionOnClick
              getRowClassName={(params) => params.row.hidden ? 'row-hidden' : ''}
              sx={{
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .row-hidden': {
                  opacity: 0.35,
                },
              }}
            />
          </CardContent>
        </Card>

        <AllProjects auth={auth} />
      </Stack>
    </Box>
  );
}
